import topApi from '../../components/api/ToPApi';
import { Menu, Product } from '../../models/interfaces';

// Modificadores y extras de productos. Una opción sin producto es gratis y va en la nota de la línea
// ("Término medio", "Sin cebolla"); una opción ligada a un producto es un extra con el precio de ese producto.

export interface ModifierOption {
    id?: number;
    name: string;
    productId: number | null;
    productName?: string | null;
    productActive?: boolean | null;
    price?: number;
}

export interface ModifierGroup {
    id?: number;
    name: string;
    minSelect: number;
    maxSelect: number;   // 0 = sin límite
    options: ModifierOption[];
    productIds: number[];
}

export const emptyGroup = (): ModifierGroup => ({
    name: '', minSelect: 0, maxSelect: 0, options: [{ name: '', productId: null }], productIds: [],
});

const errorMessage = (e: any) => e?.response?.data?.error || 'Ocurrió un error de comunicación, intente de nuevo.';

export const loadGroups = async (): Promise<ModifierGroup[]> => {
    try {
        const resp = await topApi.get<{ groups: ModifierGroup[] }>('/modifier/');
        return resp.data.groups;
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export const saveGroup = async (group: ModifierGroup): Promise<number> => {
    const body = {
        group: {
            name: group.name,
            minSelect: group.minSelect,
            maxSelect: group.maxSelect,
            options: group.options.map(o => ({ name: o.name, productId: o.productId })),
            productIds: group.productIds,
        }
    };
    try {
        const resp = group.id
            ? await topApi.put<{ id: number }>(`/modifier/${group.id}`, body)
            : await topApi.post<{ id: number }>('/modifier/', body);
        return resp.data.id;
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export const deleteGroup = async (id: number): Promise<void> => {
    try {
        await topApi.delete(`/modifier/${id}`);
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export interface ProductOption {
    id: number;
    name: string;
    price: number;
    menu: string;
}

// Productos activos de todos los menús, para asignar grupos y ligar extras
export const loadProducts = async (): Promise<ProductOption[]> => {
    try {
        const resp = await topApi.get<{ menu: Menu[] }>('/menu/getMenu');
        const flatten = (menus: Menu[], parent = ''): ProductOption[] => menus.flatMap(m => {
            const menuName = parent ? `${parent} / ${m.name}` : m.name;
            return [
                ...(m.products ?? []).map((p: Product) => ({ id: p.id, name: p.name, price: Number(p.regularPrice ?? p.price), menu: menuName })),
                ...flatten(m.submenu ?? [], menuName),
            ];
        });
        const unique = new Map(flatten(resp.data.menu ?? []).map(p => [p.id, p]));
        return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

// Resumen de la regla de selección para mostrarla en la lista y en las ventas
export const selectionRule = (g: Pick<ModifierGroup, 'minSelect' | 'maxSelect'>) => {
    const { minSelect: min, maxSelect: max } = g;
    if (min > 0 && max > 0 && min === max) return min === 1 ? 'Obligatorio, elegir 1' : `Obligatorio, elegir ${min}`;
    if (min > 0) return max > 0 ? `Obligatorio, de ${min} a ${max}` : `Obligatorio, al menos ${min}`;
    return max > 0 ? `Opcional, hasta ${max}` : 'Opcional, sin límite';
};

// Mismas validaciones que el API, para avisar antes de guardar
export const validateGroup = (g: ModifierGroup): string | null => {
    const options = g.options.filter(o => o.name.trim());
    if (!g.name.trim()) return 'Escriba el nombre del grupo.';
    if (options.length === 0) return 'Agregue al menos una opción.';
    if (g.options.some(o => !o.name.trim())) return 'Hay opciones sin nombre.';
    const names = options.map(o => o.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) return 'Hay opciones con el mismo nombre.';
    if (g.minSelect < 0 || g.maxSelect < 0) return 'El mínimo y el máximo no pueden ser negativos.';
    if (g.maxSelect > 0 && g.minSelect > g.maxSelect) return 'El mínimo no puede ser mayor que el máximo.';
    if (g.minSelect > options.length) return 'El mínimo no puede ser mayor que la cantidad de opciones.';
    return null;
};
